import { GridColDef, getGridDateOperators, getGridStringOperators } from "@mui/x-data-grid-pro";
import formatDate from "date-fns/format";
import parseDate from "date-fns/parse";
import startOfMonth from "date-fns/startOfMonth";
import { CurbsuiteTransactionLogEntry } from "common/graphql/queries/GetCurbsuiteTransactions";
import { getReconciledMonth } from "../getSessionDates";

// The only column identical across every shape, with no per-variant configuration at all.
export const TRANSACTION_INDEX_COLUMN = {
  field: "transactionIndex",
  headerName: "ID",
  type: "string",
  valueGetter({ row }) {
    return row.transaction.index ? row.transaction.index.toString().padStart(6, "0") : null;
  },
  sortingOrder: ["desc", "asc"],
  filterOperators: getGridStringOperators().filter((operator) => operator.value === "equals"),
  filterable: true,
} as GridColDef<CurbsuiteTransactionLogEntry, string>;

/**
 * The Date/Event Date/Month column, shared by every shape. `isAirportWallet` reshapes it into a
 * month-grouped "Month" column for GSP's Monthly Charges tab; `isBusStationPark` relabels it "Event
 * Date" for Philly Bus. The two flags are mutually exclusive (one is wallet-only, the other
 * park-only), but each caller only ever supplies the one relevant to its own variant.
 */
export function buildTransactionDateColumn({
  isAirportWallet,
  isBusStationPark,
  showsOperatorBilling,
}: {
  isAirportWallet: boolean;
  isBusStationPark: boolean;
  showsOperatorBilling: boolean;
}): GridColDef<CurbsuiteTransactionLogEntry, Date | null, string> {
  return {
    field: "transactionDate",
    headerName: isAirportWallet ? "Month" : isBusStationPark ? "Event Date" : "Date",
    type: "date",
    ...(showsOperatorBilling ? { width: 120 } : { flex: 1 }),
    sortingOrder: ["desc", "asc"],
    valueGetter({ row }) {
      const reconciledMonth = getReconciledMonth(row);
      if (reconciledMonth) {
        return reconciledMonth;
      }
      const transactionDate = row.transactionDate ? parseDate(row.transactionDate, "yyyy-MM-dd", new Date()) : null;
      // GSP's Monthly Charges tab is about which month was closed, so never show a bare
      // settlement date there -- fall back to that date's month so a row is never blank.
      return isAirportWallet && transactionDate ? startOfMonth(transactionDate) : transactionDate;
    },
    valueFormatter: (params) =>
      params.value ? formatDate(params.value, isAirportWallet ? "MMMM yyyy" : "MM/dd/yyyy") : "",
    filterable: true,
    filterOperators: getGridDateOperators().filter((op) => ["is", "before", "after"].includes(op.value)),
  } as GridColDef<CurbsuiteTransactionLogEntry, Date | null, string>;
}

/**
 * The Operator column, shared by bus-station-park, airport-park (both via showsOperatorBilling),
 * and airport-wallet. `width` is a vestigial hint from airport-wallet's original definition -- MUI
 * gives `flex` absolute precedence over `width` (hydrateColumnsWidth only falls back to `width` when
 * `flex` is unset), so it has never actually pinned the column's size. Keeping both, as every other
 * flex+width column in this codebase does (see DAILY_TRANSACTION_AMOUNT_COLUMN,
 * buildTripFeeAmountColumn), preserves that inert-width behavior instead of silently changing it.
 */
export function buildOperatorColumn({ width }: { width?: number } = {}): GridColDef<
  CurbsuiteTransactionLogEntry,
  string
> {
  return {
    field: "operator",
    headerName: "Operator",
    type: "string",
    valueGetter({ row }) {
      return row.payerAccount?.organizationName || "-";
    },
    flex: 1,
    ...(width ? { width } : {}),
    filterable: true,
    sortable: false,
    filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
  } as GridColDef<CurbsuiteTransactionLogEntry, string>;
}
